import { GridColDef, GridValueSetterParams } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';

import { useCallback, useState } from 'react';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { newGridRecordId } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { gridCurrencyCellNullable, gridPercentageCellNullable, dataGridCol } from '@shared/components/grid/common';
import { CONDITIONS_TYPE_OPTIONS } from '@shared/constants/lists';

import { AlertModal } from '@shared/components/modals/AlertModal';
import { TTierCondition } from '../../../../interfaces';
import { requestCopyCondition, requestCreateCondition, requestDeleteCondition, requestUpdateCondition } from './queries';

interface ITiersProps {
  lenderId: string;
  tierId?: string;
  data?: TTierCondition[];
}

export const Conditions = ({ lenderId, tierId, data = [] }: ITiersProps) => {
  const [showAlert, setShowAlert] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const roundValue = (field: string) => (params: GridValueSetterParams) => ({
    ...params.row,
    [field]: params.value === null || params.value === undefined || isNaN(params.value) ? null : Math.round(params.value),
  });

  const columns: GridColDef[] = [
    dataGridCol('rate', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('termGroup', {
      minWidth: 108,
      valueSetter: roundValue('termGroup'),
    }),
    dataGridCol('ficoGroup', { minWidth: 108 }),
    dataGridCol('maxLtvGreen', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('maxAgeGreen', { valueSetter: roundValue('maxAgeGreen') }),
    dataGridCol('maxDti', {
      ...gridPercentageCellNullable,
      flex: 1,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('maxPti', {
      ...gridPercentageCellNullable,
      flex: 1,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('minTotalAmountFinanceGreen', {
      ...gridCurrencyCellNullable,
      minWidth: 160,
      renderEditCell: (params) => (
        <GridColNumber params={params} /* Remove or correct the suffix */ decimalScale={2} />
      ),
    }),
    dataGridCol('maxLoanAmount', {
      renderEditCell: (params) => (
        <GridColNumber params={params} prefix="$" decimalScale={2} />
      ),
    }),
    dataGridCol('maxMilesGreen', { valueSetter: roundValue('maxMilesGreen') }),
    dataGridCol('maxFico', { valueSetter: roundValue('maxFico') }),
    dataGridCol('type', {
      minWidth: 160,
      type: 'string',
      renderEditCell: (params) => (
        <GridColSelect options={CONDITIONS_TYPE_OPTIONS} params={params} />
      ),
    }),
    dataGridCol('maxFlatFeePercent', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
      headerName: 'Max Flat Fee %',
      minWidth: 100,
    }),
    dataGridCol('minTotalAmountFinanceYellow', {
      ...gridCurrencyCellNullable,
      minWidth: 160,
      renderEditCell: (params) => (
        <GridColNumber params={params} decimalScale={2} />
      ),
    }),
    dataGridCol('minAgeGreen', { valueSetter: roundValue('minAgeGreen') }),
    dataGridCol('maxAgeYellow', { valueSetter: roundValue('maxAgeYellow') }),
    dataGridCol('maxMilesYellow', { valueSetter: roundValue('maxMilesYellow') }),
    dataGridCol('maxLtvYellow', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('maxMarkup', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('maxAllInLtv', {
      ...gridPercentageCellNullable,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
    dataGridCol('maxFlatGreen', {
      ...gridPercentageCellNullable,
      flex: 1,
      renderEditCell: (params) => (
        <GridColNumber params={params} suffix="%" decimalScale={2} />
      ),
    }),
  ].map((column) => ({
    minWidth: 100,
    editable: true,
    renderEditCell: (params) => <GridColNumber params={params} />,
    type: 'number',
    ...column,
  }));

  const gridActions = {
    onSave: async (row: GridValidRowModel) => {
      row?.id === newGridRecordId
        ? await requestCreateCondition(lenderId, tierId, row)
        : await requestUpdateCondition(lenderId, row);
    },
    onCopy: (row: GridValidRowModel) => setShowAlert(row?.id),
    onDelete: async (row: GridValidRowModel) => {
      await requestDeleteCondition(lenderId, row?.id);
    },
  };

  const handleAlertConfirm = useCallback(
    async () => {
      setLoading(true);
      await requestCopyCondition(lenderId, showAlert ?? '');
      setLoading(false);
      setShowAlert(null);
    },
    [lenderId, showAlert]
  );
  
  return (
    <>
      {!!showAlert && (
        <AlertModal
          title={'Alert'}
          subtitle="Copy Selected Condition?"
          open={!!showAlert}
          buttonText={'Ok'}
          isLoading={loading}
          onConfirm={handleAlertConfirm}
          onClose={() => setShowAlert(null)}
        />
      )}

      <PhoenixGrid
        autoHeight
        withHeader
        disableSelectionOnClick
        name="Condition"
        pageSize={10}
        gridId={`Condition-${lenderId}`}
        columns={columns}
        loading={!data}
        rows={data || []}
        actions={gridActions}
        disabled={!tierId}
      />
    </>
  );
};
